import {
  Box,
  Checkbox,
  Text,
  HStack,
  VStack,
  Button,
  Badge,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import { Timestamp } from 'firebase/firestore';
import { ActionItem, User } from '../../types';

interface ActionItemListItemProps {
  actionItem: ActionItem;
  users: User[];
  onEdit: () => void;
  onDelete: () => void;
  onComplete: () => void;
  onDrop: () => void;
}

/**
 * Action Item List Item Component
 * Displays a single action item with its details
 * Migrated from Tailwind to Chakra UI
 */
export function ActionItemListItem({
  actionItem,
  users,
  onEdit,
  onDelete,
  onComplete,
  onDrop,
}: ActionItemListItemProps) {
  const owner = users.find((u) => u.id === actionItem.ownerId);
  const assignee = actionItem.assignedToUserId
    ? users.find((u) => u.id === actionItem.assignedToUserId)
    : null;
  const ownerName = owner?.name || 'Unknown';
  const assigneeName = assignee?.name || null;

  const formatDate = (date: Date | string | Timestamp) => {
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    if (date && typeof date === 'object' && 'toDate' in date) {
      // Firestore Timestamp
      return (date as Timestamp).toDate().toLocaleDateString();
    }
    return new Date(date as string).toLocaleDateString();
  };

  const isOverdue = () => {
    if (actionItem.complete) return false;
    const dueDate = actionItem.dueDate instanceof Date ? actionItem.dueDate : new Date(actionItem.dueDate);
    return dueDate < new Date();
  };

  // Color mode values for dark mode support
  const cardBgComplete = useColorModeValue('green.50', 'green.900');
  const cardBgOverdue = useColorModeValue('red.50', 'red.900');
  const cardBg = useColorModeValue('white', 'gray.700');
  const textColorComplete = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const textColorLight = useColorModeValue('gray.600', 'gray.500');

  return (
    <Box
      borderLeft="4px"
      borderLeftColor={actionItem.complete ? 'green.500' : isOverdue() ? 'red.500' : 'gray.300'}
      bg={actionItem.complete ? cardBgComplete : isOverdue() ? cardBgOverdue : cardBg}
      p={4}
      borderRadius="md"
      shadow="sm"
    >
      <Flex justify="space-between" align="start">
        <HStack spacing={3} flex={1} align="start">
          <Checkbox
            isChecked={actionItem.complete}
            onChange={onComplete}
            colorScheme="green"
            mt={1}
          />
          <VStack align="start" spacing={1} flex={1}>
            <Text
              fontSize="md"
              fontWeight="medium"
              textDecoration={actionItem.complete ? 'line-through' : 'none'}
              color={actionItem.complete ? textColorComplete : textColor}
            >
              {actionItem.description}
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              <Text fontSize="sm" color={textColorLight}>
                Owner: {ownerName}
              </Text>
              {assigneeName && (
                <Text fontSize="sm" color={textColorLight}>
                  Assigned: {assigneeName}
                </Text>
              )}
              <Text fontSize="sm" color={textColorLight}>
                Due: {formatDate(actionItem.dueDate)}
              </Text>
              {isOverdue() && !actionItem.complete && (
                <Badge colorScheme="red" size="sm">
                  Overdue
                </Badge>
              )}
              {actionItem.complete && actionItem.completedDate && (
                <Badge colorScheme="green" size="sm">
                  Completed {formatDate(actionItem.completedDate)}
                </Badge>
              )}
              {actionItem.linkedRockId && (
                <Badge colorScheme="blue" size="sm">
                  Linked to Rock
                </Badge>
              )}
              {actionItem.linkedIssueId && (
                <Badge colorScheme="orange" size="sm">
                  Linked to Issue
                </Badge>
              )}
              {actionItem.linkedMetricId && (
                <Badge colorScheme="purple" size="sm">
                  Linked to Metric
                </Badge>
              )}
            </HStack>
          </VStack>
        </HStack>

        <HStack spacing={2}>
          <Button
            size="xs"
            variant="link"
            colorScheme="blue"
            onClick={onEdit}
          >
            Edit
          </Button>
          <Button
            size="xs"
            variant="link"
            colorScheme="red"
            onClick={onDrop}
          >
            Drop
          </Button>
        </HStack>
      </Flex>
    </Box>
  );
}
